'use client';
import React from 'react';
import { TypePlayKitPageTranslations } from '@/utils/translations/types/TypePlayKitPageTranslations';
import { HeaderSize, HeaderText } from '@/components/ui/HeaderText';
import { PlayKitCard } from '@/components/account';
import classNames from 'classnames';
import OrderDetails from '../PlayKitSchedule/components/OrderDetails/OrderDetails';
import {
  generateBookAddonLink,
  getBookBundleUpsellProductDetails,
  TypeScheduledItemGroup,
} from '@/components/account/PlayKitSchedule/utils';
import {
  BookBundleUpsell,
  PrepayUpsellBanners,
} from '@/components/account/PlayKitSchedule/components';
import useLocale from '@/hooks/useLocale';
import interpolate from '@/utils/translations/interpolate';
import { formatMoney } from '@/utils/helpers';
import OrderInsights from '../PlayKitSchedule/components/OrderInsights/OrderInsights';
import { TypePlayKitBundleOffer } from '@/lib/api/productCatalog/types';
import { TypeBanner } from '@/core/generated-types';

export interface NextPlayKitProps {
  t: TypePlayKitPageTranslations;
  group: TypeScheduledItemGroup;
  isFirstGroup: boolean;
  hasManyGroups: boolean;
  prepayBundleExtensionOffers: TypePlayKitBundleOffer[];
  prepayBanner?: TypeBanner;
  holidayBanner?: TypeBanner;
}

function NextPlayKit({
  t,
  group,
  hasManyGroups,
  isFirstGroup,
  prepayBundleExtensionOffers,
  prepayBanner,
  holidayBanner,
}: NextPlayKitProps) {
  const locale = useLocale();
  const isPrepaid = group.items.length > 1;
  const { shouldShowBookBundleUpsell, bookBundleUpsellProducts } =
    getBookBundleUpsellProductDetails(group);

  function determineHeadingText() {
    if (group.isUpcomingOrderPreview) {
      return t.nextPayment;
    }

    if (isFirstGroup) {
      if (hasManyGroups) {
        return t.nextOrders;
      }
      return t.nextOrder;
    }

    return '';
  }

  const headingText = determineHeadingText();

  return (
    <div data-testid="nextPlayKit">
      <HeaderText
        data-testid="nextPlayKitGroupHeading"
        classes="pb-1 md:pb-2"
        size={HeaderSize.H1}
        text={headingText}
      />
      {group.savingsText && (
        <p className="mb-4 text-base">{group.savingsText}</p>
      )}
      {group.order && (
        <OrderInsights
          t={t}
          group={group}
          locale={locale}
          showShippingAddress
          showEditShippingAddress={
            group.subscriptionShippingAddressUuid ===
            group.order.shipping_address.original_uuid
          }
          showNextPaymentDate={group.isUpcomingOrderPreview}
          showPrepaidDate={!group.isUpcomingOrderPreview}
          showOrderNumber={!group.isUpcomingOrderPreview}
        />
      )}
      <PrepayUpsellBanners
        group={group}
        prepayBundleExtensionOffers={prepayBundleExtensionOffers}
        prepayBanner={prepayBanner}
        holidayBanner={holidayBanner}
      />
      <div
        className={classNames(
          '-translate-x-4 md:translate-x-0 w-screen md:w-full p-6 md:p-8 mb-10 md:rounded-md',
          {
            'bg-indigo-tint ': isPrepaid,
            'shadow-lg': !isPrepaid,
          },
        )}
      >
        {group.items.map((item, index) => {
          const isLastKitInGroup = index === group.items.length - 1;

          return (
            <PlayKitCard
              t={t}
              key={item.uuid}
              scheduledItem={item}
              isLastKitInGroup={isLastKitInGroup}
            >
              {isLastKitInGroup && shouldShowBookBundleUpsell && (
                <BookBundleUpsell
                  title={t.bookBundleAddOn}
                  linkHref={generateBookAddonLink(group, locale)}
                  linkCopy={interpolate(t.addBookBundles, {
                    bundleAmount: formatMoney(
                      bookBundleUpsellProducts[0].metadata.price_cents / 100,
                      locale,
                      true,
                    ),
                  })}
                  bookBundleUpsellProducts={bookBundleUpsellProducts}
                />
              )}
            </PlayKitCard>
          );
        })}
        {group.isUpcomingOrderPreview && <OrderDetails t={t} group={group} />}
      </div>
    </div>
  );
}

export default NextPlayKit;
